import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import { ApiResponse, TagGroup } from '@vision/ui/interfaces';
import type { AccountTag } from '@vision/ui/interfaces/account-tag';
import { buildEndpointWithQuery } from '@vision/ui/utils';
import { axiosBaseQuery } from './base.service';
import {
  fromApiTag,
  fromApiTagGroup,
  toSaveTagGroupRequestDto,
  toTagGroupsRequestDto,
  toTagsRequestDto,
} from './tag.service.mappers';
import type {
  AccountTagResponseDto,
  GetTagGroupsArgs,
  GetTagsArgs,
  SaveTagGroupInput,
  TagGroupResponseDto,
} from './tag.service.types';

export const TagService = createApi({
  reducerPath: 'TagService',
  // Tag selectors can be imported while the shared base query is still initializing.
  baseQuery: (...args: Parameters<ReturnType<typeof axiosBaseQuery>>) => axiosBaseQuery()(...args),
  tagTypes: ['TagGroups', 'Tags'],
  endpoints: (builder) => ({
    getTagGroups: builder.query<ApiResponse<TagGroup[]>, GetTagGroupsArgs>({
      query: ({ nodeId, query, ...args }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/tag_groups`, query),
        method: HttpMethod.GET,
        params: toTagGroupsRequestDto({ nodeId, ...args }),
      }),
      transformResponse: (response: ApiResponse<TagGroupResponseDto[]>): ApiResponse<TagGroup[]> => ({
        ...response,
        data: response.data.map(fromApiTagGroup),
      }),
      providesTags: ['TagGroups'],
    }),
    fetchTagWithPagination: builder.query<ApiResponse<AccountTag[]>, GetTagsArgs>({
      query: ({ nodeId, query, ...args }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/tag_groups/tags`, query),
        method: HttpMethod.GET,
        params: toTagsRequestDto({ nodeId, ...args }),
      }),
      transformResponse: (response: ApiResponse<AccountTagResponseDto[]>): ApiResponse<AccountTag[]> => ({
        ...response,
        data: response.data.map(fromApiTag),
      }),
      providesTags: ['Tags'],
    }),
    createTagGroup: builder.mutation<ApiResponse<TagGroup>, { nodeId: string; data: SaveTagGroupInput }>({
      query: ({ nodeId, data }) => ({
        url: `/v2/accounts/${nodeId}/tag_groups`,
        method: HttpMethod.POST,
        data: toSaveTagGroupRequestDto(data),
      }),
      transformResponse: (response: ApiResponse<TagGroupResponseDto>): ApiResponse<TagGroup> => ({
        ...response,
        data: fromApiTagGroup(response.data),
      }),
      invalidatesTags: ['TagGroups', 'Tags'],
    }),
    updateTagGroup: builder.mutation<
      ApiResponse<TagGroup>,
      { nodeId: string; data: SaveTagGroupInput & { id: string } }
    >({
      query: ({ nodeId, data }) => ({
        url: `/v2/accounts/${nodeId}/tag_groups/${data.id}`,
        method: HttpMethod.POST,
        data: toSaveTagGroupRequestDto(data),
      }),
      transformResponse: (response: ApiResponse<TagGroupResponseDto>): ApiResponse<TagGroup> => ({
        ...response,
        data: fromApiTagGroup(response.data),
      }),
      invalidatesTags: ['TagGroups', 'Tags'],
    }),
    deleteTagGroup: builder.mutation<void, { nodeId: string; tagGroupId: string }>({
      query: ({ nodeId, tagGroupId }) => ({
        url: `/v2/accounts/${nodeId}/tag_groups/${tagGroupId}/delete`,
        method: HttpMethod.POST,
      }),
      invalidatesTags: ['TagGroups', 'Tags'],
    }),
  }),
});

export const {
  useCreateTagGroupMutation,
  useDeleteTagGroupMutation,
  useLazyGetTagGroupsQuery,
  useLazyFetchTagWithPaginationQuery,
  useFetchTagWithPaginationQuery,
  useUpdateTagGroupMutation,
} = TagService;
